import {
  useCreateHolidayMutation,
  useDeleteHolidayMutation,
  useGetMonthlyHolidaysQuery,
  useGetMonthlyLeavesQuery,
  useUpdateHolidayMutation,
} from '@/app/features/employee/emp-leave/emp-leave.api';
import IMAGES from '@/assets/images';
import CustomDrawer from '@/components/CustomDrawer';
import CustomImage from '@/components/CustomImage';
import EmptyState from '@/components/EmptyState';
import ConfirmationModal from '@/components/ConfirmationModal';
import useAuth from '@/hooks/useAuth';
import {
  Calendar03Icon,
  ChevronDownIcon,
  PencilEdit02Icon,
  TrashIcon,
} from '@/icons';
import { toast } from '@/lib/toast';
import { errorToast } from '@/utils';
import { Button, Calendar, Flex, Form, Input, Select, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { holidayTypes } from '../../../_data/data';
import NextIcon from '../../_icons/NextIcon';
import PreviousIcon from '../../_icons/PreviousIcon';

export default function EditCalendar({
  open,
  handleClose,
}: {
  data?: any;
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
}) {
  const { organizationId } = useAuth();
  const [form] = Form.useForm();
  const [createHoliday, { isLoading: isCreating }] = useCreateHolidayMutation();
  const [updateHoliday, { isLoading: isUpdating }] = useUpdateHolidayMutation();
  const [deleteHoliday, { isLoading: isDeleting }] = useDeleteHolidayMutation();
  const [value, setValue] = useState(dayjs());
  const [selected, setSelected] = useState(dayjs());
  const [editingHoliday, setEditingHoliday] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: leavesData, isFetching: isLeavesFetching } =
    useGetMonthlyLeavesQuery(
      {
        params: {
          organization_id: organizationId!,
          start_date: selected.format('YYYY-MM-DD'),
          end_date: selected.format('YYYY-MM-DD'),
        },
      },
      { skip: !organizationId || !open },
    );

  const { data: holidaysData, isFetching: isHolidaysFetching } =
    useGetMonthlyHolidaysQuery(
      {
        params: {
          organizationId: organizationId!,
          month: value.month() + 1, // dayjs is 0-indexed
          year: value.year(),
        },
      },
      { skip: !organizationId || !open },
    );

  const leaves = leavesData?.data?.data || [];
  const selectedDateStr = selected.format('YYYY-MM-DD');
  const holidays = (holidaysData?.data?.data || []).filter(
    (h) => dayjs(h.date).format('YYYY-MM-DD') === selectedDateStr,
  );

  const onSelect = (newValue: any) => {
    setValue(newValue);
    setSelected(newValue);
    // Reset edit mode on date change
    setEditingHoliday(null);
    form.resetFields();
  };

  const handleEdit = (holiday: any) => {
    setEditingHoliday(holiday);
    form.setFieldsValue({
      holidayType: holiday.type,
      name: holiday.name,
      description: holiday.description,
    });
  };

  const handleDelete = (id: string) => {
    setDeletingId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!organizationId || !deletingId) return;
    try {
      const res = await deleteHoliday(deletingId).unwrap();
      if (res.success) {
        toast('success', 'Holiday deleted successfully');
        setDeletingId(null);
      } else {
        toast('error', res?.message || 'Failed to delete holiday');
      }
    } catch (error) {
      errorToast(error, 'Failed to delete holiday');
    }
  };

  const handleSubmit = async (values: any) => {
    if (!organizationId) {
      errorToast(null, 'Organization ID not found');
      return;
    }

    try {
      const holidayData = {
        organizationId,
        name: values.name,
        date: selected.format('YYYY-MM-DD'),
        type: values.holidayType,
        description: values.description || '',
        recurring: false, // Defaulting as per previous pattern
      };

      if (editingHoliday) {
        const res = await updateHoliday({
          id: editingHoliday.id,
          data: holidayData,
        }).unwrap();

        if (res.success) {
          toast('success', 'Holiday updated successfully');
          setEditingHoliday(null);
          form.resetFields();
          handleClose();
        } else {
          toast('error', res?.message || 'Failed to update holiday');
        }
        return;
      }

      const res = await createHoliday(holidayData).unwrap();

      if (res.success === true) {
        toast('success', 'Holiday created successfully');
      } else {
        toast('error', res?.data?.message || 'Failed to create holyday');
      }
      form.resetFields();
      handleClose();
    } catch (error) {
      errorToast(error, `Failed to ${editingHoliday ? 'update' : 'create'} holiday`);
    }
  };

  return (
    <>
      <CustomDrawer
        width={850}
        open={open}
        onClose={handleClose}
        title={'Edit Calendar'}
      >
        <div className="w-full bg-transparent flex justify-center items-start  text-white">
          <div className="">
            <div className="w-full dark:bg-theme-slate-800 border dark:border-theme-gray-700 rounded-xl text-white p-3">
              {/* Ant Design Calendar */}
              {/* Custom Calendar Header */}
              <div className="flex items-start justify-center mb-4 text-xl font-semibold ">
                <button
                  onClick={() => setValue(value.subtract(1, 'month'))}
                  className="px-2 text-gray-300 hover:text-white text-base mt-2"
                >
                  <PreviousIcon />
                </button>

                <div className="flex-col justify-center text-center">
                  <div>{value.format('MMMM')}</div>
                  <div className="text-white text-sm font-bold">
                    {value.format('YYYY')}
                  </div>
                </div>

                <button
                  onClick={() => setValue(value.add(1, 'month'))}
                  className="px-2 text-gray-300 hover:text-white text-base mt-2"
                >
                  <NextIcon />
                </button>
              </div>

              {/* Ant Design Calendar */}
              <Calendar
                fullscreen={false}
                value={value}
                onSelect={onSelect}
                onPanelChange={(val) => setValue(val)}
                headerRender={() => null}
                className="custom-calendar"
              />
            </div>

            {/* Selected Date Header */}
            <div className="mt-6 mb-4">
              <div className="text-lg font-bold border-b border-gray-700 pb-2">
                {selected.format('DD MMMM, YYYY')}
              </div>
            </div>

            {/* Form Start */}
            <Form
              form={form}
              size="large"
              layout="vertical"
              className="custom-form"
              onFinish={handleSubmit}
            >
              <Form.Item
                label={<span className="text-gray-300">Holiday Type</span>}
                name="holidayType"
                rules={[
                  { required: true, message: 'Please select holiday type' },
                ]}
              >
                <Select
                  placeholder="Select Type"
                  className="w-full"
                  suffixIcon={<ChevronDownIcon className="text-white size-6" />}
                  options={holidayTypes}
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-gray-300">Name</span>}
                name="name"
                rules={[
                  { required: true, message: 'Please enter holiday name' },
                ]}
              >
                <Input
                  placeholder="Write Here..."
                  className=" bg-[#0f172a] text-white"
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-gray-300">Description</span>}
                name="description"
                rules={[
                  {
                    required: true,
                    message: 'Please select holiday description',
                  },
                ]}
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Write Here..."
                  className="bg-[#0f172a] text-white"
                />
              </Form.Item>

              <Flex align="center" justify="end" gap={16} className="mt-6">
                <Button
                  type="default"
                  className="!bg-transparent !text-teal-500 !border !border-teal-500 text-base font-bold"
                  onClick={() => {
                    handleClose();
                    setEditingHoliday(null);
                    form.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  className="!text-[#444444] !text-base !font-semibold !bg-[#00C6AE]"
                  htmlType="submit"
                  loading={isCreating || isUpdating}
                >
                  {editingHoliday ? 'Update' : 'Done'}
                </Button>
              </Flex>
            </Form>

            {/* Showcase Section Below Form */}
            <div className="mt-10 border-t border-gray-700 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs font-black text-gray-400 uppercase tracking-widest">
                  Activities on this date
                </div>
                <div className="bg-theme-primary/10 text-theme-primary text-[10px] px-2 py-0.5 rounded font-bold">
                  {leaves.length + holidays.length} Records
                </div>
              </div>

              {isLeavesFetching || isHolidaysFetching ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="p-4 rounded-xl border border-theme-gray-700 bg-theme-slate-900/40"
                    >
                      <Skeleton active avatar paragraph={{ rows: 1 }} />
                    </div>
                  ))}
                </div>
              ) : leaves.length === 0 && holidays.length === 0 ? (
                <EmptyState
                  className="py-5"
                  title="No Activities Scheduled"
                  description="There are no leaves or holidays recorded for this date."
                  icon={
                    <Calendar03Icon size={48} className="text-theme-gray-600" />
                  }
                />
              ) : (
                <div className="space-y-3">
                  {/* Holidays */}
                  {holidays.map((h) => (
                    <Flex
                      key={h.id}
                      align="center"
                      justify="space-between"
                      className={`p-4 rounded-xl border transition-all duration-300 ${
                        editingHoliday?.id === h.id
                          ? 'bg-theme-primary/10 border-theme-primary'
                          : 'bg-theme-slate-900/40 border-theme-gray-700 hover:border-theme-primary/40'
                      }`}
                    >
                      <Flex align="center" gap={12}>
                        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-theme-primary">
                          <Calendar03Icon size={20} />
                        </div>
                        <div>
                          <div className="text-white text-[15px] font-bold">
                            {h.name}
                            <span className="ml-2 text-[10px] text-theme-primary font-normal bg-theme-primary/10 px-1.5 py-0.5 rounded uppercase">
                              {h.type}
                            </span>
                          </div>
                          <div className="text-gray-400 text-xs mt-0.5 font-medium">
                            Holiday Entry
                          </div>
                        </div>
                      </Flex>
                      <Flex align="center" gap={10}>
                        <button
                          onClick={() => handleEdit(h)}
                          className="text-theme-primary hover:text-white transition-colors"
                        >
                          <PencilEdit02Icon size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(h.id)}
                          disabled={isDeleting}
                          className="text-theme-gray-500 hover:text-red-400 transition-colors disabled:opacity-50"
                        >
                          <TrashIcon size={18} />
                        </button>
                      </Flex>
                    </Flex>
                  ))}

                  {/* Leaves */}
                  {leaves.map((l) => (
                    <Flex
                      key={l.id}
                      align="center"
                      justify="space-between"
                      className="p-4 rounded-xl bg-theme-slate-900/40 border border-theme-gray-700 hover:border-teal-500/40 transition-all duration-300"
                    >
                      <Flex align="center" gap={12}>
                        <div className="relative">
                          <CustomImage
                            src={l.employee?.photo || IMAGES.avatar}
                            width={40}
                            height={40}
                            className="rounded-full object-cover"
                          />
                        </div>
                        <div>
                          <div className="text-white text-[15px] font-bold">
                            {l.employee?.name}
                          </div>
                          <div className="text-theme-primary text-xs mt-0.5 font-semibold">
                            {l.leave_type} — {l.num_days} Days
                          </div>
                        </div>
                      </Flex>
                      <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider bg-white/5 px-2 py-1 rounded">
                        Leave
                      </div>
                    </Flex>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </CustomDrawer>

      <ConfirmationModal
        open={!!deletingId}
        handleClose={() => setDeletingId(null)}
        handleSubmit={handleDeleteConfirm}
        loading={isDeleting}
        title="Delete Holiday"
        subTitle="Are you sure you want to delete this holiday? This action cannot be undone."
        buttonName="Delete"
      />
    </>
  );
}
